import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Image as ImageIcon,
  Megaphone,
  CalendarDays,
  Quote,
  Award,
  Sparkles,
  Download,
  RefreshCw,
  Wand2,
  Loader2,
} from "lucide-react";

const TEMPLATES = [
  { id: "vote", label: "Vote / Announce", icon: Megaphone, sizes: ["square", "story"], blurb: "Big bold 'Vote for me' graphic with your name, office, and election day." },
  { id: "event", label: "Event", icon: CalendarDays, sizes: ["square", "story"], blurb: "Event details — title, date, time, location — branded with your name." },
  { id: "quote", label: "Quote", icon: Quote, sizes: ["square"], blurb: "A short candidate quote with attribution." },
  { id: "endorsement", label: "Endorsement", icon: Award, sizes: ["square"], blurb: "Endorser quote and credit, branded for your campaign." },
];

const PALETTES = [
  { id: "red", label: "Red", swatch: "#B91C29" },
  { id: "navy", label: "Navy", swatch: "#0B1B3B" },
  { id: "ivory", label: "Ivory", swatch: "#FCFAF5", border: true },
];

const FIELD_DEFS = {
  vote: [],
  event: [
    { key: "title", label: "Event title", placeholder: "Kickoff Rally at the VFW Hall" },
    { key: "event_date", label: "Date", placeholder: "Sat Oct 12" },
    { key: "event_time", label: "Time", placeholder: "6:00 PM" },
    { key: "location", label: "Location", placeholder: "VFW Post 256, Canandaigua" },
  ],
  quote: [
    { key: "quote", label: "Quote", placeholder: "We deserve a county that works for the people who live in it.", multiline: true },
  ],
  endorsement: [
    { key: "endorser_name", label: "Endorser name", placeholder: "Sheriff John Roberts" },
    { key: "endorser_title", label: "Endorser title / role", placeholder: "Ontario County Sheriff" },
    { key: "quote", label: "Endorsement quote", placeholder: "Jane Candidate is the right leader for our county.", multiline: true },
  ],
};

export default function Graphics() {
  const [campaign, setCampaign] = useState(null);
  const [template, setTemplate] = useState("vote");
  const [palette, setPalette] = useState("red");
  const [size, setSize] = useState("square");
  const [fields, setFields] = useState({});
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [isRendering, setIsRendering] = useState(false);
  const [isDrafting, setIsDrafting] = useState(false);

  useEffect(() => {
    api.get("/campaign").then(({ data }) => setCampaign(data || null)).catch(() => setCampaign(null));
  }, []);

  // When template changes, ensure size is supported.
  useEffect(() => {
    const def = TEMPLATES.find((t) => t.id === template);
    if (def && !def.sizes.includes(size)) setSize("square");
    setFields({});
    setImageUrl(null);
  }, [template]);

  const tpl = useMemo(() => TEMPLATES.find((t) => t.id === template), [template]);
  const fieldDefs = FIELD_DEFS[template];

  const setField = (k, v) => setFields((p) => ({ ...p, [k]: v }));

  const generate = async () => {
    setIsRendering(true);
    setImageUrl(null);
    try {
      const res = await api.post(
        "/graphics/render",
        { template, palette, size, fields },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      setImageUrl(url);
    } catch (err) {
      // axios with responseType:blob returns Blob even on error — best-effort parse for a friendlier message
      let detail = err.message;
      try {
        const text = await err.response?.data?.text?.();
        if (text) detail = formatApiErrorDetail(JSON.parse(text).detail) || detail;
      } catch (parseErr) {
        // intentional: if the blob isn't readable JSON, fall through with the original err.message
        console.debug("Could not parse graphics-render error blob:", parseErr);
      }
      toast.error(detail);
    } finally {
      setIsRendering(false);
    }
  };

  const draft = async () => {
    setIsDrafting(true);
    try {
      const { data } = await api.post("/graphics/draft", { template, description });
      setFields((p) => ({ ...p, ...(data?.fields || {}) }));
      toast.success("Draft populated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsDrafting(false);
    }
  };

  const download = () => {
    if (!imageUrl) return;
    const a = document.createElement("a");
    a.href = imageUrl;
    a.download = `electable-${template}-${size}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="graphics-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold uppercase tracking-wider text-flag-red">Graphics</p>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.16em] bg-flag-red text-white px-2 py-0.5 rounded-full" data-testid="graphics-beta-badge">
              <Sparkles className="h-3 w-3" /> Beta
            </span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-flag-navy mt-1">On-brand campaign graphics, in seconds.</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Pick a template, fill in the content, download as a PNG. Branded automatically with your name, office, and district. New and still improving — tell us what would make this more useful.
          </p>
        </div>
      </div>

      {/* Template picker */}
      <section className="mt-8" data-testid="graphics-template-row">
        <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-slate-700 mb-3">Template</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {TEMPLATES.map((t) => {
            const Icon = t.icon;
            const active = t.id === template;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTemplate(t.id)}
                data-testid={`graphics-template-${t.id}`}
                className={`text-left p-4 rounded-xl border-2 transition-all ${active ? "border-flag-red bg-flag-red/5" : "border-slate-200 bg-white hover:border-flag-navy"}`}
              >
                <div className={`h-9 w-9 rounded-md flex items-center justify-center ${active ? "bg-flag-red text-white" : "bg-flag-navy/5 text-flag-navy"}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="mt-3 font-display font-bold text-flag-navy">{t.label}</div>
                <div className="mt-1 text-xs text-slate-600 leading-snug">{t.blurb}</div>
              </button>
            );
          })}
        </div>
      </section>

      <div className="mt-8 grid lg:grid-cols-5 gap-6">
        {/* Left: controls */}
        <div className="lg:col-span-2 space-y-6">
          {/* AI draft helper */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="graphics-ai-draft-card">
            <div className="flex items-center gap-2">
              <Wand2 className="h-4 w-4 text-flag-red" />
              <h3 className="font-display font-bold text-flag-navy">Draft with AI (optional)</h3>
            </div>
            <p className="text-sm text-slate-600 mt-1">Describe what this graphic is about — Advisor will fill in the text, in your voice.</p>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="graphics-draft-description"
              placeholder={`e.g. ${template === "event" ? "Kickoff rally Saturday night at the VFW" : template === "quote" ? "A line about lowering county taxes" : template === "endorsement" ? "Sheriff Roberts endorses me" : "Vote for me — short rallying line"}`}
              rows={2}
              className="mt-3 text-sm"
            />
            <Button
              onClick={draft}
              disabled={isDrafting}
              data-testid="graphics-draft-btn"
              variant="outline"
              className="mt-3 h-10 border-2 font-semibold"
            >
              {isDrafting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Drafting…</> : <><Wand2 className="h-4 w-4 mr-2" /> Draft text</>}
            </Button>
          </div>

          {/* Content fields */}
          {fieldDefs.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="graphics-fields-card">
              <h3 className="font-display font-bold text-flag-navy">Content</h3>
              <div className="mt-3 space-y-3">
                {fieldDefs.map((f) => (
                  <div key={f.key}>
                    <Label htmlFor={`g-${f.key}`} className="text-xs font-semibold text-slate-800">{f.label}</Label>
                    {f.multiline ? (
                      <Textarea
                        id={`g-${f.key}`}
                        value={fields[f.key] || ""}
                        onChange={(e) => setField(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        data-testid={`graphics-field-${f.key}`}
                        rows={3}
                        className="mt-1 text-sm"
                      />
                    ) : (
                      <Input
                        id={`g-${f.key}`}
                        value={fields[f.key] || ""}
                        onChange={(e) => setField(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        data-testid={`graphics-field-${f.key}`}
                        className="mt-1 h-10 text-sm"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Brand fill-in (read-only summary) */}
          {campaign && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm text-sm" data-testid="graphics-brand-summary">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Pulled from your profile</div>
              <div className="mt-2 text-slate-800">
                <div><span className="text-slate-500">Name:</span> <span className="font-semibold">{campaign.account_type === "committee" ? campaign.committee_name : campaign.candidate_name}</span></div>
                {campaign.office_sought && <div><span className="text-slate-500">Office:</span> {campaign.office_sought}</div>}
                {campaign.district && <div><span className="text-slate-500">District:</span> {campaign.district}</div>}
                {campaign.election_date && <div><span className="text-slate-500">Election:</span> {campaign.election_date}</div>}
              </div>
            </div>
          )}

          {/* Palette + size */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="graphics-style-card">
            <h3 className="font-display font-bold text-flag-navy">Style</h3>
            <div className="mt-3">
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Color</div>
              <div className="mt-2 flex gap-2">
                {PALETTES.map((p) => {
                  const active = palette === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPalette(p.id)}
                      data-testid={`graphics-palette-${p.id}`}
                      title={p.label}
                      className={`h-10 w-10 rounded-full border-2 transition-all ${active ? "border-flag-navy ring-2 ring-flag-navy/30" : p.border ? "border-slate-300" : "border-transparent"}`}
                      style={{ backgroundColor: p.swatch }}
                    />
                  );
                })}
              </div>
            </div>
            {tpl?.sizes.length > 1 && (
              <div className="mt-4">
                <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Size</div>
                <div className="mt-2 flex gap-2">
                  {tpl.sizes.map((s) => {
                    const active = size === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSize(s)}
                        data-testid={`graphics-size-${s}`}
                        className={`h-10 px-3 rounded-md border-2 text-sm font-semibold ${active ? "border-flag-red bg-flag-red/5 text-flag-navy" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
                      >
                        {s === "square" ? "Square · 1080" : "Story · 1080×1920"}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Generate */}
          <Button
            onClick={generate}
            disabled={isRendering}
            data-testid="graphics-generate-btn"
            className="w-full h-12 bg-flag-red hover:bg-flag-red-hover text-white font-bold"
          >
            {isRendering ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Rendering…</> : imageUrl ? <><RefreshCw className="h-4 w-4 mr-2" /> Regenerate</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate graphic</>}
          </Button>
        </div>

        {/* Right: preview */}
        <div className="lg:col-span-3" data-testid="graphics-preview-pane">
          <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-8 shadow-sm">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h3 className="font-display font-bold text-flag-navy">Preview</h3>
              {imageUrl && (
                <Button
                  onClick={download}
                  data-testid="graphics-download-btn"
                  variant="outline"
                  className="h-10 border-2 font-semibold"
                >
                  <Download className="h-4 w-4 mr-2" /> Download PNG
                </Button>
              )}
            </div>
            <div
              className="mt-4 rounded-lg bg-slate-50 border border-dashed border-slate-300 flex items-center justify-center overflow-hidden"
              style={{ aspectRatio: size === "story" ? "9 / 16" : "1 / 1", maxHeight: "640px" }}
              data-testid="graphics-preview-frame"
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt="Generated graphic preview"
                  data-testid="graphics-preview-image"
                  className="max-h-full max-w-full"
                />
              ) : (
                <div className="text-center px-6">
                  <ImageIcon className="h-8 w-8 text-slate-400 mx-auto" />
                  <p className="mt-3 text-sm text-slate-600">Pick a template, drop in your content, hit <span className="font-semibold">Generate graphic</span>.</p>
                </div>
              )}
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-3 px-1" data-testid="graphics-beta-note">
            Beta — these templates are intentionally simple to start. Text is rendered cleanly server-side so spelling and layout stay correct. If a layout feels off for your race, send feedback and we&apos;ll tighten it.
          </p>
        </div>
      </div>
    </div>
  );
}
